import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

const FR = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 4 });

interface Props {
  price: number | null | undefined;
  variation?: number | null;
  currency?: string | null;
  /** "scrape" = price captured when we scraped the message (≈ authoring time).
   *  "daily"  = daily close for that date (forum_history). */
  source?: "scrape" | "daily" | string | null;
  className?: string;
}

/**
 * Tiny inline "price at the time" chip shown next to a post's timestamp or a
 * news article's date. Renders NOTHING when no price is known (per spec), so
 * callers can drop it unconditionally.
 *
 * A tooltip explains the precision: an exact intraday quote at authoring time
 * was never recorded, so we show either the scrape-time price (close to when we
 * saw the message) or the day's closing price.
 */
export function PriceAtTime({ price, variation, currency, source, className }: Props) {
  if (price == null) return null;

  const cur = currency || "";
  const sign = variation == null ? 0 : Math.sign(variation);
  const varColor =
    sign > 0 ? "text-emerald-500" : sign < 0 ? "text-red-500" : "text-foreground-muted";
  const VarIcon = sign > 0 ? ArrowUp : sign < 0 ? ArrowDown : null;

  const hint =
    source === "daily"
      ? "Cours de clôture du jour (cotation intraday non disponible)."
      : "Cours relevé au moment où le message a été collecté.";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-md bg-surface px-1.5 py-0.5 text-[11px] tabular-nums text-foreground-muted",
            className,
          )}
        >
          <span className="font-medium text-foreground">
            {FR.format(price)}
            {cur ? ` ${cur}` : ""}
          </span>
          {variation != null && (
            <span className={cn("inline-flex items-center", varColor)}>
              {VarIcon && <VarIcon className="size-2.5" />}
              {variation > 0 ? "+" : ""}
              {variation.toFixed(2)}%
            </span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}
